import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import * as imagesApi from '../../api/channel/images';
import { upload } from '../../api/upload';
import { axiosMutationWrapper, axiosQueryWrapper } from '../utils/axios-wrapper';
import { GFX_QUERY_KEYS } from './gfx';

const ITEMS_PER_PAGE = 20;

export const IMAGE_QUERY_KEYS = {
	fetchImages: (channelId, mode, itemsPerPage, currentPage) => ['api', 'channel', 'images', 'fetch', { channelId, mode, itemsPerPage, currentPage }],
	fetchImagesBase: () => ['api', 'channel', 'images', 'fetch'],
};

export const useFetchImages = (
	channelId,
	mode,
	itemsPerPage,
	currentPage,
) => useQuery(
	IMAGE_QUERY_KEYS.fetchImages(channelId, mode, itemsPerPage, currentPage),
	axiosQueryWrapper(imagesApi.fetchImages, channelId, mode, itemsPerPage, currentPage),
);

export const useInfinityFetchImages = (
	channelId,
	mode,
	itemsPerPage = ITEMS_PER_PAGE,
) => useInfiniteQuery(
	IMAGE_QUERY_KEYS.fetchImages(channelId, mode, itemsPerPage),
	({ pageParam = 1 }) => axiosQueryWrapper(
		imagesApi.fetchImages,
		channelId,
		mode,
		itemsPerPage,
		pageParam,
	)(),
	{
		getNextPageParam: (lastPage, allPages) => {
			const currentItemsLenght = allPages.reduce((acc, curr) => acc + curr.data.length, 0);
			if (currentItemsLenght < lastPage.totalItemsCount) {
				return allPages.length + 1;
			}
			return undefined;
		},
	},
);

export const useUploadImage = () => {
	const queryClient = useQueryClient();

	return useMutation(
		async ({ channelId, image, onUploadProgress }) => {
			const { blob } = image;
			const filename = await upload(blob, onUploadProgress);
			if (!filename || filename.error) throw filename;

			const imageToCreate = {
				isGfx: image.isGfx,
				label: image.label,
				isShared: image.isShared || undefined,
				mimeType: blob.type,
				filename,
				fileSize: blob.size,
				layerConfig: image.layerConfig,
			};

			return axiosMutationWrapper(imagesApi.createImage)({
				channelId,
				image: imageToCreate,
			});
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(IMAGE_QUERY_KEYS.fetchImagesBase());
				queryClient.invalidateQueries(GFX_QUERY_KEYS.fetchGfx());
			},
		},
	);
};

export const useUpdateImage = () => {
	const queryClient = useQueryClient();

	return useMutation(
		({ _id, label, layerConfig }) => (
			axiosMutationWrapper(imagesApi.updateImage)({ _id, label, layerConfig })
		),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(IMAGE_QUERY_KEYS.fetchImagesBase());
				queryClient.invalidateQueries(GFX_QUERY_KEYS.fetchGfx());
			},
		},
	);
};

export const useDeleteImage = () => {
	const queryClient = useQueryClient();

	return useMutation(
		axiosMutationWrapper(imagesApi.deleteImage),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(IMAGE_QUERY_KEYS.fetchImagesBase());
				queryClient.invalidateQueries(GFX_QUERY_KEYS.fetchGfx());
			},
		},
	);
};
